import { useQuery } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { z } from "zod";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const schema = z.object({
  linked: z.boolean(),
  standing: z
    .enum(["visitor", "attending", "in_process", "member", "inactive"])
    .nullable()
    .optional(),
  since: z.string().nullable().optional(),
  pathway: z
    .object({ id: z.string(), name: z.string(), description: z.string().nullable() })
    .nullable()
    .optional(),
  steps: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().nullable(),
        completedOn: z.string().nullable(),
      }),
    )
    .optional(),
});
export type MyPathway = z.infer<typeof schema>;

export const STANDING_WORDS: Record<NonNullable<NonNullable<MyPathway["standing"]>>, string> = {
  visitor: "You are visiting",
  attending: "You are part of us",
  in_process: "You are on the way to membership",
  member: "You are a member",
  inactive: "We have not seen you for a while",
};

/** Where I am on the church's pathway (task 14, the member's half). Resolved from the login on the server. */
export function useMyPathway() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["my-pathway", tenantId],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<MyPathway> => {
      const { data, error } = await supabase.rpc("my_pathway", { p_tenant_id: tenantId ?? "" });
      if (error) throw error;
      return schema.parse(data);
    },
  });
}
